import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';

// dashboard routing
const DashboardDefault = Loadable(lazy(() => import('views/dashboard/Default')));
const DashboardAnalytics = Loadable(lazy(() => import('views/dashboard/Analytics')));

// widget routing
const WidgetStatistics = Loadable(lazy(() => import('views/widget/Statistics')));
const WidgetData = Loadable(lazy(() => import('views/widget/Data')));
const WidgetChart = Loadable(lazy(() => import('views/widget/Chart')));

// application - user social & account profile routing
const AppUserSocialProfile = Loadable(lazy(() => import('views/application/users/social-profile')));
const AppUserAccountProfile1 = Loadable(lazy(() => import('views/application/users/account-profile/Profile1')));
const AppUserAccountProfile2 = Loadable(lazy(() => import('views/application/users/account-profile/Profile2')));
const AppUserAccountProfile3 = Loadable(lazy(() => import('views/application/users/account-profile/Profile3')));

// application - user cards & list variant routing
const AppProfileCardStyle1 = Loadable(lazy(() => import('views/application/users/card/CardStyle1')));
const AppProfileCardStyle2 = Loadable(lazy(() => import('views/application/users/card/CardStyle2')));
const AppProfileCardStyle3 = Loadable(lazy(() => import('views/application/users/card/CardStyle3')));
const AppProfileListStyle1 = Loadable(lazy(() => import('views/application/users/list/Style1')));
const AppProfileListStyle2 = Loadable(lazy(() => import('views/application/users/list/Style2')));

// application - customer routing
const AppCustomerList = Loadable(lazy(() => import('views/application/customer/CustomerList')));
const AppCustomerOrderList = Loadable(lazy(() => import('views/application/customer/OrderList')));
const AppCustomerCreateInvoice = Loadable(lazy(() => import('views/application/customer/CreateInvoice')));
const AppCustomerOrderDetails = Loadable(lazy(() => import('views/application/customer/OrderDetails')));
const AppCustomerProduct = Loadable(lazy(() => import('views/application/customer/Product')));
const AppCustomerProductReview = Loadable(lazy(() => import('views/application/customer/ProductReview')));

// application routing
const AppChat = Loadable(lazy(() => import('views/application/chat')));
const AppKanban = Loadable(lazy(() => import('views/application/kanban')));
const AppKanbanBacklogs = Loadable(lazy(() => import('views/application/kanban/Backlogs')));
const AppKanbanBoard = Loadable(lazy(() => import('views/application/kanban/Board')));
const AppMail = Loadable(lazy(() => import('views/application/mail')));
const AppCalendar = Loadable(lazy(() => import('views/application/calendar')));
const AppContactCard = Loadable(lazy(() => import('views/application/contact/Card')));
const AppContactList = Loadable(lazy(() => import('views/application/contact/List')));

// application e-commerce pages
const AppECommProducts = Loadable(lazy(() => import('views/application/e-commerce/Products')));
const AppECommProductDetails = Loadable(lazy(() => import('views/application/e-commerce/ProductDetails')));
const AppECommProductList = Loadable(lazy(() => import('views/application/e-commerce/ProductList')));
const AppECommCheckout = Loadable(lazy(() => import('views/application/e-commerce/Checkout')));

// forms component routing
const FrmComponentsTextfield = Loadable(lazy(() => import('views/forms/components/TextField')));
const FrmComponentsButton = Loadable(lazy(() => import('views/forms/components/Button')));
const FrmComponentsCheckbox = Loadable(lazy(() => import('views/forms/components/Checkbox')));
const FrmComponentsRadio = Loadable(lazy(() => import('views/forms/components/Radio')));
const FrmComponentsSwitch = Loadable(lazy(() => import('views/forms/components/Switch')));
const FrmComponentsAutoComplete = Loadable(lazy(() => import('views/forms/components/AutoComplete')));
const FrmComponentsSlider = Loadable(lazy(() => import('views/forms/components/Slider')));
const FrmComponentsDateTime = Loadable(lazy(() => import('views/forms/components/DateTime')));

// forms plugins layout
const FrmLayoutLayout = Loadable(lazy(() => import('views/forms/layouts/Layouts')));
const FrmLayoutMultiColumnForms = Loadable(lazy(() => import('views/forms/layouts/MultiColumnForms')));
const FrmLayoutActionBar = Loadable(lazy(() => import('views/forms/layouts/ActionBar')));
const FrmLayoutStickyActionBar = Loadable(lazy(() => import('views/forms/layouts/StickyActionBar')));

// forms plugins routing
const FrmAutocomplete = Loadable(lazy(() => import('views/forms/plugins/AutoComplete')));
const FrmMask = Loadable(lazy(() => import('views/forms/plugins/Mask')));
const FrmClipboard = Loadable(lazy(() => import('views/forms/plugins/Clipboard')));
const FrmRecaptcha = Loadable(lazy(() => import('views/forms/plugins/Recaptcha')));
const FrmWysiwugEditor = Loadable(lazy(() => import('views/forms/plugins/WysiwugEditor')));
const FrmModal = Loadable(lazy(() => import('views/forms/plugins/Modal')));
const FrmTooltip = Loadable(lazy(() => import('views/forms/plugins/Tooltip')));

// table routing
const TableBasic = Loadable(lazy(() => import('views/forms/tables/TableBasic')));
const TableDense = Loadable(lazy(() => import('views/forms/tables/TableDense')));
const TableEnhanced = Loadable(lazy(() => import('views/forms/tables/TableEnhanced')));
const TableData = Loadable(lazy(() => import('views/forms/tables/TableData')));
const TableCustomized = Loadable(lazy(() => import('views/forms/tables/TablesCustomized')));
const TableStickyHead = Loadable(lazy(() => import('views/forms/tables/TableStickyHead')));
const TableCollapsible = Loadable(lazy(() => import('views/forms/tables/TableCollapsible')));

// forms validation
const FrmFormsValidation = Loadable(lazy(() => import('views/forms/forms-validation')));
const FrmFormsWizard = Loadable(lazy(() => import('views/forms/forms-wizard')));

// chart routing
const ChartApexchart = Loadable(lazy(() => import('views/forms/chart/Apexchart')));
const OrgChartPage = Loadable(lazy(() => import('views/forms/chart/OrgChart')));

// basic ui-elements routing
const BasicUIAccordion = Loadable(lazy(() => import('views/ui-elements/basic/UIAccordion')));
const BasicUIAvatar = Loadable(lazy(() => import('views/ui-elements/basic/UIAvatar')));
const BasicUIBadges = Loadable(lazy(() => import('views/ui-elements/basic/UIBadges')));
const BasicUIBreadcrumb = Loadable(lazy(() => import('views/ui-elements/basic/UIBreadcrumb')));
const BasicUICards = Loadable(lazy(() => import('views/ui-elements/basic/UICards')));
const BasicUIChip = Loadable(lazy(() => import('views/ui-elements/basic/UIChip')));
const BasicUIList = Loadable(lazy(() => import('views/ui-elements/basic/UIList')));
const BasicUITabs = Loadable(lazy(() => import('views/ui-elements/basic/UITabs')));

// advance ui-elements routing
const AdvanceUIAlert = Loadable(lazy(() => import('views/ui-elements/advance/UIAlert')));
const AdvanceUIDialog = Loadable(lazy(() => import('views/ui-elements/advance/UIDialog')));
const AdvanceUIPagination = Loadable(lazy(() => import('views/ui-elements/advance/UIPagination')));
const AdvanceUIProgress = Loadable(lazy(() => import('views/ui-elements/advance/UIProgress')));
const AdvanceUIRating = Loadable(lazy(() => import('views/ui-elements/advance/UIRating')));
const AdvanceUISnackbar = Loadable(lazy(() => import('views/ui-elements/advance/UISnackbar')));
const AdvanceUISkeleton = Loadable(lazy(() => import('views/ui-elements/advance/UISkeleton')));
const AdvanceUISpeeddial = Loadable(lazy(() => import('views/ui-elements/advance/UISpeeddial')));
const AdvanceUITimeline = Loadable(lazy(() => import('views/ui-elements/advance/UITimeline')));
const AdvanceUIToggleButton = Loadable(lazy(() => import('views/ui-elements/advance/UIToggleButton')));
const AdvanceUITreeview = Loadable(lazy(() => import('views/ui-elements/advance/UITreeview')));

// pricing page routing
const PagesPrice1 = Loadable(lazy(() => import('views/pages/pricing/Price1')));
const PagesPrice2 = Loadable(lazy(() => import('views/pages/pricing/Price2')));

// utilities routing
const UtilsTypography = Loadable(lazy(() => import('views/utilities/Typography')));
const UtilsColor = Loadable(lazy(() => import('views/utilities/Color')));
const UtilsShadow = Loadable(lazy(() => import('views/utilities/Shadow')));
const UtilsAnimation = Loadable(lazy(() => import('views/utilities/Animation')));
const UtilsGrid = Loadable(lazy(() => import('views/utilities/Grid')));

// sample page routing
const SamplePage = Loadable(lazy(() => import('views/sample-page')));



// Master Pages
const DashboardScreen = Loadable(lazy(() => import('../mago_pages/dashboard/index')));
const BrandWiseBarChartScreen = Loadable(lazy(() => import('../mago_pages/brandwiseBarchart/index')));
const DistrictTable =  Loadable(lazy(() => import('../mago_pages/DistrictScreen')));
const StateTable =  Loadable(lazy(() => import('../mago_pages/StateScreen')));
const CityTable =  Loadable(lazy(() => import('../mago_pages/CityScreen')));
const BrandTable =  Loadable(lazy(() => import('../mago_pages/brandscreen')));
const CategoriesTable =  Loadable(lazy(() => import('../mago_pages/categoriesscreen')));
const ProducttypeTable =  Loadable(lazy(() => import('../mago_pages/ProducttypeScreen')));
const ProductTable = Loadable(lazy(() => import('../mago_pages/ProductScreen')));
const ExpenseScreen =  Loadable(lazy(() => import('../mago_pages/ExpenseScreen')));
const EmployeeRegistrationTable =  Loadable(lazy(() => import('../mago_pages/EmployeeRegistrationScreen')));
const BrandWiseSetTarget =  Loadable(lazy(() => import('../mago_pages/brandwisesettargetscreen')));
const ProductWatchList = Loadable(lazy(() => import('../mago_pages/productwatchlist')));

// Daily Activity
const ManualAttendanceScreen = Loadable(lazy(() => import('../mago_pages/manualattendance')));
const MonthlyCalenderScreen = Loadable(lazy(() => import('../mago_pages/monthlycalender')));
const MonthlyPlannerScreen = Loadable(lazy(() => import('../mago_pages/monthlyplanner')));
const MonthlyPlannerAdminScreen = Loadable(lazy(() => import('../mago_pages/monthlyplannerAdmin')));

// Transaction
const PaymentScreen = Loadable(lazy(() => import('../mago_pages/PaymentScreen')));
const ExpenseEntryScreen =  Loadable(lazy(() => import('../mago_pages/expenseentryscreen')));
const ExpenseEntryAdminScreen =  Loadable(lazy(() => import('../mago_pages/expenseentryadminscreen')));
const PaymenReminderScreen = Loadable(lazy(() => import('../mago_pages/PaymentReminderScreen')));
const CustomerEntryTable =  Loadable(lazy(() => import('../mago_pages/CustomerEntryScreen')));
const OrderEntryScreen = Loadable(lazy(() => import('../mago_pages/OrderScreen')));
const PurchaseorderScreen = Loadable(lazy(() => import('../mago_pages/purchaseorderScreen')));
const InvoiceEntryScreen = Loadable(lazy(() => import('../mago_pages/invoicescreen')));
const QuotationEntryScreen = Loadable(lazy(()=> import('../mago_pages/QuotationScreen')));

// Reports
const CustomerReportsScreen = Loadable(lazy(() => import('../reports/customerreports')));
const PaymentReportsScreen = Loadable(lazy(() => import('../reports/paymentreports')));
const AttendanceReportsScreen = Loadable(lazy(() => import('../reports/attendancereports')));
const OrderReportsScreen = Loadable(lazy(() => import('../reports/orderreports')));
const LocationReportsScreen = Loadable(lazy(() => import('../reports/locationreports')));
const LedgerReportsScreen = Loadable(lazy(() => import('../reports/ledgerreports')));
const TargetReportsScreen = Loadable(lazy(() => import('../reports/monthlytargetreports')));
const PunchinReportsScreen = Loadable(lazy(() => import('../reports/punchinreports')));
const DaybookReportsScreen = Loadable(lazy(() => import('../reports/daybookreports')));
const StockReportsScreen = Loadable(lazy(() => import('../reports/stockreports')));
const DailyReportsScreen = Loadable(lazy(() => import('../reports/dailyreports')));
const ExpenseReportScreen = Loadable(lazy(() => import('../reports/ExpenseReport')));
const BrandwiseReport = Loadable(lazy(() => import('../reports/brandwisereports')));
const PurchaseorderPendingReports = Loadable(lazy(() => import('../reports/Purchaseorderpendingreport')));
const Biometricreport = Loadable(lazy(()=> import('../reports/biometricreport')))
const Deliveryreport = Loadable(lazy(()=> import('../reports/deliveryreports')))

const Editqtyscreen = Loadable(lazy(() => import('../mago_pages/component/EditqtyComp')));
// Page allocation pages
const PageAllocationScreen = Loadable(lazy(() => import('../mago_pages/pageallocationscreen')));
// home icon page
const WelcomeMagoPrinpro =  Loadable(lazy(() => import('../ui-component/extended/Welcome')));


// Production 
const Formulascreen = Loadable(lazy(() => import('../mago_pages/formula')) )
const Productionscreen = Loadable(lazy(() => import('../mago_pages/production')) )


// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/',
    element: (
        <AuthGuard>
            <MainLayout />
        </AuthGuard>
    ),
    children: [
        {
          path: '/magopages/welcome',
            element: <WelcomeMagoPrinpro />
        },
        {
            path: '/magopages/Editqtyscreen',
            element: <Editqtyscreen />
        },
        {
            path: '/widget/statistics',
            element: <WidgetStatistics />
        },
        {
            path: '/widget/data',
            element: <WidgetData />
        },
        {
            path: '/widget/chart',
            element: <WidgetChart />
        },

        {
            path: '/user/social-profile/:tab',
            element: <AppUserSocialProfile />
        },
        {
            path: '/user/account-profile/profile1',
            element: <AppUserAccountProfile1 />
        },
        {
            path: '/user/account-profile/profile2',
            element: <AppUserAccountProfile2 />
        },
        {
            path: '/user/account-profile/profile3',
            element: <AppUserAccountProfile3 />
        },

        {
            path: '/user/card/card1',
            element: <AppProfileCardStyle1 />
        },
        {
            path: '/user/card/card2',
            element: <AppProfileCardStyle2 />
        },
        {
            path: '/user/card/card3',
            element: <AppProfileCardStyle3 />
        },
        {
            path: '/user/list/list1',
            element: <AppProfileListStyle1 />
        },
        {
            path: '/user/list/list2',
            element: <AppProfileListStyle2 />
        },

        {
            path: '/customer/customer-list',
            element: <AppCustomerList />
        },
        {
            path: '/customer/order-list',
            element: <AppCustomerOrderList />
        },
        {
            path: '/customer/create-invoice',
            element: <AppCustomerCreateInvoice />
        },
        {
            path: '/customer/order-details',
            element: <AppCustomerOrderDetails />
        },
        {
            path: '/customer/product',
            element: <AppCustomerProduct />
        },
        {
            path: '/customer/product-review',
            element: <AppCustomerProductReview />
        },

        {
            path: '/app/chat',
            element: <AppChat />
        },
        {
            path: '/app/mail',
            element: <AppMail />
        },
        {
            path: '/app/kanban',
            element: <AppKanban />,
            children: [
                {
                    path: 'backlogs',
                    element: <AppKanbanBacklogs />
                },
                {
                    path: 'board',
                    element: <AppKanbanBoard />
                }
            ]
        },
        {
            path: '/app/calendar',
            element: <AppCalendar />
        },
        {
            path: '/app/contact/c-card',
            element: <AppContactCard />
        },
        {
            path: '/app/contact/c-list',
            element: <AppContactList />
        },

        {
            path: '/e-commerce/products',
            element: <AppECommProducts />
        },
        {
            path: '/e-commerce/product-details/:id',
            element: <AppECommProductDetails />
        },
        {
            path: '/e-commerce/product-list',
            element: <AppECommProductList />
        },
        {
            path: '/e-commerce/checkout',
            element: <AppECommCheckout />
        },

        {
            path: '/components/text-field',
            element: <FrmComponentsTextfield />
        },
        {
            path: '/components/button',
            element: <FrmComponentsButton />
        },
        {
            path: '/components/checkbox',
            element: <FrmComponentsCheckbox />
        },
        {
            path: '/components/radio',
            element: <FrmComponentsRadio />
        },
        {
            path: '/components/autocomplete',
            element: <FrmComponentsAutoComplete />
        },
        {
            path: '/components/slider',
            element: <FrmComponentsSlider />
        },
        {
            path: '/components/switch',
            element: <FrmComponentsSwitch />
        },
        {
            path: '/components/date-time',
            element: <FrmComponentsDateTime />
        },

        {
            path: '/forms/layouts/layouts',
            element: <FrmLayoutLayout />
        },
        {
            path: '/forms/layouts/multi-column-forms',
            element: <FrmLayoutMultiColumnForms />
        },
        {
            path: '/forms/layouts/action-bar',
            element: <FrmLayoutActionBar />
        },
        {
            path: '/forms/layouts/sticky-action-bar',
            element: <FrmLayoutStickyActionBar />
        },

        {
            path: '/forms/frm-autocomplete',
            element: <FrmAutocomplete />
        },
        {
            path: '/forms/frm-mask',
            element: <FrmMask />
        },
        {
            path: '/forms/frm-clipboard',
            element: <FrmClipboard />
        },
        {
            path: '/forms/frm-recaptcha',
            element: <FrmRecaptcha />
        },
        {
            path: '/forms/frm-wysiwug',
            element: <FrmWysiwugEditor />
        },
        {
            path: '/forms/frm-modal',
            element: <FrmModal />
        },
        {
            path: '/forms/frm-tooltip',
            element: <FrmTooltip />
        },

        {
            path: '/tables/tbl-basic',
            element: <TableBasic />
        },
        {
            path: '/tables/tbl-dense',
            element: <TableDense />
        },
        {
            path: '/tables/tbl-enhanced',
            element: <TableEnhanced />
        },
        {
            path: '/tables/tbl-data',
            element: <TableData />
        },
        {
            path: '/tables/tbl-customized',
            element: <TableCustomized />
        },
        {
            path: '/tables/tbl-sticky-header',
            element: <TableStickyHead />
        },
        {
            path: '/tables/tbl-collapse',
            element: <TableCollapsible />
        },

        {
            path: 'forms/charts/apexchart',
            element: <ChartApexchart />
        },
        {
            path: '/forms/charts/orgchart',
            element: <OrgChartPage />
        },
        {
            path: '/forms/forms-validation',
            element: <FrmFormsValidation />
        },
        {
            path: '/forms/forms-wizard',
            element: <FrmFormsWizard />
        },

        {
            path: '/basic/accordion',
            element: <BasicUIAccordion />
        },
        {
            path: '/basic/avatar',
            element: <BasicUIAvatar />
        },
        {
            path: '/basic/badges',
            element: <BasicUIBadges />
        },
        {
            path: '/basic/breadcrumb',
            element: <BasicUIBreadcrumb />
        },
        {
            path: '/basic/cards',
            element: <BasicUICards />
        },
        {
            path: '/basic/chip',
            element: <BasicUIChip />
        },
        {
            path: '/basic/list',
            element: <BasicUIList />
        },
        {
            path: '/basic/tabs',
            element: <BasicUITabs />
        },

        {
            path: '/advance/alert',
            element: <AdvanceUIAlert />
        },
        {
            path: '/advance/dialog',
            element: <AdvanceUIDialog />
        },
        {
            path: '/advance/pagination',
            element: <AdvanceUIPagination />
        },
        {
            path: '/advance/progress',
            element: <AdvanceUIProgress />
        },
        {
            path: '/advance/rating',
            element: <AdvanceUIRating />
        },
        {
            path: '/advance/snackbar',
            element: <AdvanceUISnackbar />
        },
        {
            path: '/advance/skeleton',
            element: <AdvanceUISkeleton />
        },
        {
            path: '/advance/speeddial',
            element: <AdvanceUISpeeddial />
        },
        {
            path: '/advance/timeline',
            element: <AdvanceUITimeline />
        },
        {
            path: '/advance/toggle-button',
            element: <AdvanceUIToggleButton />
        },
        {
            path: '/advance/treeview',
            element: <AdvanceUITreeview />
        },

        {
            path: '/pages/price/price1',
            element: <PagesPrice1 />
        },
        {
            path: '/pages/price/price2',
            element: <PagesPrice2 />
        },

        {
            path: '/utils/util-typography',
            element: <UtilsTypography />
        },
        {
            path: '/utils/util-color',
            element: <UtilsColor />
        },
        {
            path: '/utils/util-shadow',
            element: <UtilsShadow />
        },
        {
            path: '/utils/util-animation',
            element: <UtilsAnimation />
        },
        {
            path: '/utils/util-grid',
            element: <UtilsGrid />
        },
        {
            path: '/sample-page',
            element: <SamplePage />
        },
        {
            path: '/dashboard/default',
            element: <DashboardDefault />
        },
        {
            path: '/dashboard/analytics',
            element: <DashboardAnalytics />
        },
        {
            path: '/magopages/state',
            element: <StateTable />
        },
        {
            path: '/magopages/city',
            element: <CityTable />
        },
        {
            path: '/magopages/brand',
            element: <BrandTable />
        },
        {
            path: '/magopages/category',
            element: <CategoriesTable />
        },
        {
            path: '/magopages/producttype',
            element: <ProducttypeTable />
        },
        {
            path: '/magopages/district',
            element: <DistrictTable />
        },
        {
            path: '/magopages/employee',
            element: <EmployeeRegistrationTable />
        },
        {
            path: '/magopages/brandwisesettarget',
            element: <BrandWiseSetTarget />
        },
        {
            path: '/magopages/customer',
            element: <CustomerEntryTable />
        },
        {
            path: '/magopages/product',
            element: <ProductTable />
        },
        {
            path:'/magopages/order',
            element:<OrderEntryScreen />
        },
        {
            path:'/magopages/quotation',
            element:<QuotationEntryScreen/>
        },
        {
            path:'/magopages/manualattendance',
            element:<ManualAttendanceScreen />
        },
        {
            path:'/magopages/monthlycalender',
            element:<MonthlyCalenderScreen />
        },
        {
            path:'/magopages/locationreports',
            element:<LocationReportsScreen />
        },
        {
            path:'/magopages/customerreports',
            element:<CustomerReportsScreen />
        },
        {
            path:'/magopages/orderreports',
            element:<OrderReportsScreen />
        },
        {
            path:'/magopages/paymentreports',
            element:<PaymentReportsScreen />
        },
        {
            path:'/magopages/attendancereports',
            element:<AttendanceReportsScreen />
        },
        {
            path:'/magopages/ledgerreports',
            element:<LedgerReportsScreen />
        },
        {
            path:'/magopages/daybookreports',
            element:<DaybookReportsScreen />
        },
        {
            path:'/magopages/stockreports',
            element:<StockReportsScreen />
        },
        {
            path:'/magopages/targetreports',
            element:<TargetReportsScreen />
        },
        {
            path:'/magopages/punchinreports',
            element:<PunchinReportsScreen />
        },
        {
            path:'/magopages/paymentscreen',
            element:<PaymentScreen />
        },
        {
            path:'/magopages/paymentreminder',
            element:<PaymenReminderScreen />
        },
        {
            path: '/magopages/expense',
            element: <ExpenseScreen />
        },
        {
            path: '/magopages/expenseentryA',
            element: <ExpenseEntryAdminScreen />
        },
        {
            path: '/magopages/expenseentry',
            element: <ExpenseEntryScreen />
        },
        {
            path: '/magopages/dailyreports',
            element: <DailyReportsScreen />
        },
        {
            path: '/magopages/expensereports',
            element: <ExpenseReportScreen />
        },
        {
            path: '/magopages/purchaseorder',
            element: <PurchaseorderScreen />
        },
        {
            path: '/magopages/brandwisereport',
            element: <BrandwiseReport />
        },
        {
            path: '/magopages/invoiceentry',
            element: <InvoiceEntryScreen />
        },
        {
            path: '/magopages/monthlyplanner',
            element: <MonthlyPlannerScreen/>
        },
        {
            path: '/magopages/monthlyplannerA',
            element: <MonthlyPlannerAdminScreen/>
        },
        {
            path: '/magopages/dashboard',
            element: <DashboardScreen/>
        },
        {
            path: '/magopages/brandwisebarchart',
            element: <BrandWiseBarChartScreen/>
        },
        {
            path: '/magopages/pageallocation',
            element: <PageAllocationScreen/>
        },
        {
             path: '/magopages/purchaseorderpendingreport',
            element: <PurchaseorderPendingReports/>
        },
        {
            path: '/magopages/biometricreport',
            element: <Biometricreport />
        },
        {
            path: '/magopages/deliveryreport',
            element: <Deliveryreport />
        },
        {
            path: '/magopages/productwatchlist',
            element: <ProductWatchList />
        },
        {
            path: '/magopages/productformula',
            element: <Formulascreen/>
        },        
        {
            path: '/magopages/productorder',
            element: <Productionscreen/>
        },        
    ]
};

export default MainRoutes;
